import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

import { Modal } from '@/components/ui/Modal';
import { createQuizSlide, createRow, createSlide } from '@/lib/factories';
import { ROW_LAYOUT_OPTIONS } from '@/lib/row-layout-options';
import type { Slide, RowLayout } from '@/lib/schema.types';
import {
  SLIDE_TEMPLATES,
  TEMPLATE_CATEGORIES,
  type SlideTemplate,
} from '@/lib/slide-templates';
import { cn } from '@/lib/utils';
import { useEditorActions, useEditorStore } from '@/store/editor.store';

/** Beat ids already in use in the current pack — so freshly minted ids don't collide. */
function collectBeatIds(slides: Slide[]): Set<string> {
  const ids = new Set<string>();
  for (const s of slides) {
    if (s.beatMeta?.beatId) ids.add(s.beatMeta.beatId);
  }
  return ids;
}

/**
 * In pack mode every slide must carry `beatMeta` so the store's `insertSlide`
 * can splice it into the routing graph (rewire predecessor → new, new →
 * successor). Templates build script-shaped slides (no beatMeta), so wrap the
 * built content in a fresh `teach` pack-slide scaffold (id + beatMeta) while
 * keeping the template's rows.
 */
function toPackTeachSlide(built: Slide, existingBeatIds: Set<string>): Slide {
  const scaffold = createSlide({ kind: 'teach', label: built.label, existingBeatIds });
  return { ...scaffold, rows: built.rows };
}

type NewSlideInsertContextValue = {
  /** Opens the new-slide picker (blank layout or template) at optional index. */
  requestNewSlide: (index?: number) => void;
};

const NewSlideInsertContext = createContext<NewSlideInsertContextValue | null>(null);

function closePicker(
  setOpen: (v: boolean) => void,
  setInsertAtIndex: (v: number | undefined) => void,
) {
  setOpen(false);
  setInsertAtIndex(undefined);
}

/**
 * Provides the "Add new slide" picker (blank 1/2/3-column layouts + the
 * pedagogical template gallery). Mount once around the editor workspace, then
 * call `useRequestBlankSlide().requestNewSlide(index?)` from any add-slide
 * entry point. Both paths feed the store's `insertSlide(slide, index)` — the
 * same call the AI-Suggest flow uses — so a template slide (no beatMeta) drops
 * cleanly into both script and pack mode.
 */
export function BlankSlideInsertProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [insertAtIndex, setInsertAtIndex] = useState<number | undefined>(undefined);
  const actions = useEditorActions();

  const requestNewSlide = useCallback((index?: number) => {
    setInsertAtIndex(index);
    setOpen(true);
  }, []);

  const onPickLayout = (layout: RowLayout) => {
    const { mode, script } = useEditorStore.getState();
    const blankRows = [createRow(layout)];
    // Pack mode: scaffold as a teach beat so insertSlide routes it in.
    const blank: Slide =
      mode === 'pack'
        ? {
            ...createSlide({
              kind: 'teach',
              label: 'Untitled slide',
              existingBeatIds: collectBeatIds(script?.slides ?? []),
            }),
            rows: blankRows,
          }
        : { ...createSlide({ label: 'Untitled slide' }), rows: blankRows };
    actions.insertSlide(blank, insertAtIndex);
    closePicker(setOpen, setInsertAtIndex);
  };

  const onPickTemplate = (template: SlideTemplate) => {
    const { mode, script } = useEditorStore.getState();
    const existingBeatIds = collectBeatIds(script?.slides ?? []);

    let slide: Slide;
    if (mode === 'pack') {
      // MCQ → a real quiz slide (kind:"quiz" + branches + thresholds), not a
      // free-floating mcq element on a content slide.
      slide =
        template.id === 'mcq'
          ? createQuizSlide({ existingBeatIds })
          : toPackTeachSlide(template.build(), existingBeatIds);
    } else {
      slide = template.build();
    }
    // `insertSlide` selects the new slide and (in pack mode, given beatMeta)
    // rewires predecessor → new → successor automatically.
    actions.insertSlide(slide, insertAtIndex);
    closePicker(setOpen, setInsertAtIndex);
  };

  return (
    <NewSlideInsertContext.Provider value={{ requestNewSlide }}>
      {children}
      <Modal
        open={open}
        onClose={() => closePicker(setOpen, setInsertAtIndex)}
        title="Add new slide"
        description="Start from a blank layout or pick a teaching template. You can edit everything after inserting."
        width="lg"
      >
        <div className="max-h-[min(70vh,560px)] space-y-6 overflow-y-auto pr-1 scroll-thin">
          <section>
            <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-fg-subtle">
              Blank slide
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {ROW_LAYOUT_OPTIONS.map((opt) => (
                <button
                  key={opt.layout}
                  type="button"
                  onClick={() => onPickLayout(opt.layout)}
                  className={cn(
                    'flex flex-col gap-2 rounded-lg border border-border bg-white p-2 text-left transition',
                    'hover:border-accent/60 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30',
                  )}
                >
                  <div className="relative h-16 w-full overflow-hidden rounded-md bg-bg-subtle">
                    {opt.preview}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-fg">{opt.label}</p>
                    <p className="text-[11px] text-fg-subtle">{opt.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </section>

          {TEMPLATE_CATEGORIES.map((cat) => {
            const items = SLIDE_TEMPLATES.filter((t) => t.category === cat.id && !t.hidden);
            if (items.length === 0) return null;
            return (
              <section key={cat.id}>
                <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-fg-subtle">
                  {cat.label}
                </h3>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {items.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => onPickTemplate(t)}
                      title={t.description}
                      className={cn(
                        'flex flex-col gap-1.5 rounded-md border border-border bg-white p-1.5 text-left transition',
                        'hover:border-accent/60 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30',
                      )}
                    >
                      <div className="relative h-14 w-full overflow-hidden rounded bg-bg-subtle">
                        {t.preview}
                      </div>
                      <span className="line-clamp-2 px-0.5 pb-0.5 text-[11px] font-medium leading-snug text-fg-muted">
                        {t.name}
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </Modal>
    </NewSlideInsertContext.Provider>
  );
}

export function useRequestBlankSlide(): NewSlideInsertContextValue {
  const ctx = useContext(NewSlideInsertContext);
  if (!ctx) {
    throw new Error('useRequestBlankSlide must be used within BlankSlideInsertProvider');
  }
  return ctx;
}
